import axios from 'axios';
import { Registration, RegistrationStatus, RegistrationType } from '@la/types';
import {
  getLAHostnameParts,
  getProperty,
  isPlayerRegistration,
} from '@la/utilities';
import { extractAccessToken } from 'lib/auth/auth';
import { getBaseURL } from 'lib/utils/urlUtils';

const baseUrl = getBaseURL();

const getUserRegistrations = async ({
  programId,
  siteDomain,
}: {
  programId: number;
  siteDomain: string;
}) => {
  const endpoint = `${baseUrl}/api/member-portal/${siteDomain}/registration`;
  const token = extractAccessToken();
  const response = await axios.get<{ userRegistrations: Registration[] }>(
    endpoint,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        programId,
      },
    }
  );

  return response.data;
};

/**
 * Retrieves the existing user registration (if there is one).
 * @param registrationType Determines what criteria to check when getting the user registration.
 * @param userId Id of the user the registration should be under.
 * @param programId Id of the program the registration should be under. Used when
 * `registrationType` is `STAFF`.
 * @param teamIdOg Id of the team the registration should be under. Used when `registrationType`
 * is `PLAYER`.
 * @param roleId Id of the role the registration should be under. Used when `registrationType` is `STAFF`.
 * @returns The existing `UserRegistration` or `undefined` if the criteria is not met.
 */
const getExistingUserRegistration = async ({
  registrationType,
  userId,
  programId,
  teamIdOg,
  roleId,
  registrationId,
}: {
  registrationType: RegistrationType;
  userId: number;
  programId: number;
  teamIdOg: number | undefined;
  roleId?: number;
  registrationId?: string | null;
}): Promise<Registration | undefined> => {
  const { subdomain } = getLAHostnameParts();
  const { userRegistrations } = await getUserRegistrations({
    programId,
    siteDomain: subdomain,
  });

  if (registrationId) {
    return userRegistrations.find(
      (userRegistration) => userRegistration.id === registrationId
    );
  }

  return userRegistrations.find((userRegistration) => {
    const { properties } = userRegistration;

    const hasMatchingTypeAndStatus =
      userRegistration.registrationType === registrationType &&
      userRegistration.registrationStatus !== RegistrationStatus.Removed;

    if (hasMatchingTypeAndStatus) {
      if (isPlayerRegistration(registrationType)) {
        const registeredUserId =
          getProperty('registeredUserId', properties)?.[0] ??
          userRegistration.registeredUserId;
        return (
          userRegistration.teamIdOg === teamIdOg &&
          registeredUserId?.toString() === userId.toString()
        );
      }

      const programStaffId =
        getProperty('programStaffId', properties)?.[0] ??
        // DADS NG->OG mapping: programStaffId -> program_staff_id
        getProperty('program_staff_id', properties)?.[0];
      return (
        programStaffId === roleId?.toString() &&
        userRegistration.teamIdOg === teamIdOg &&
        userRegistration.registeringUserId === userId
      );
    }

    return false;
  });
};

export { getUserRegistrations, getExistingUserRegistration };
